import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, MessageCircle, MoreHorizontal, Phone } from 'lucide-react';
import type {
  Customer,
  CustomerSortField,
  CustomFieldType,
  ResolvedCustomField,
} from '@tailoring-crm/shared';
import { formatCurrency, formatDate, formatMobile } from '../../lib/format.js';
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tailoring-crm/ui';

/**
 * The customer book as a sheet.
 *
 * It used to be a column of cards, on the argument that this screen answers "find this ONE
 * person" rather than "what is the state of everything". The search box still answers the first
 * question; the sheet is what lets the same screen answer the second — who has not been back,
 * who is worth most, whose GSTIN is missing — by reading down a column. Two lists that looked
 * nothing alike for the same kind of record was also two things to learn.
 *
 * The grid: every cell ruled, every column a
 * fixed width, the row numbers and the name pinned to the left, the menu pinned to the right,
 * the heading row pinned to the top, and a sideways scroll rather than a reflow on a phone.
 *
 * WHICH columns is the shop's, from the Customers column of Settings → Fields — built-ins it may
 * rename, hide or keep off the table, and fields of its own. After them comes "Assigned to" — who
 * is looking after the customer, which the list filters on — and then three that are not fields
 * at all: orders, lifetime value and last order. Those are figures the orders flow keeps
 * on the record, never typed by anybody, so there is nothing to configure — and they are the
 * three a shop sorts this book by, which is why they are always there.
 */

/** The computed columns. Keys that no configured field uses, so they can share the sort map. */
const STAT_KEYS = ['orders', 'value', 'lastOrder'] as const;
type StatKey = (typeof STAT_KEYS)[number];

/**
 * What each clickable heading sorts by. A custom field has no index, and neither do most
 * built-ins; the book still OPENS on "recently updated", which has no column of its own.
 */
const SORT_FIELD_OF: Readonly<Record<string, CustomerSortField>> = Object.freeze({
  name: 'name',
  orders: 'orderCount',
  value: 'lifetimeValue',
  lastOrder: 'lastOrder',
});

/**
 * How wide each column is, in rem. A figure rather than `auto`, because a fixed table is the only
 * kind whose columns do not shift as rows scroll in.
 */
const COLUMN_REM: Readonly<Record<string, number>> = Object.freeze({
  name: 12,
  primaryPhone: 11,
  kind: 7,
  gstin: 11,
  email: 14,
  address: 14,
  tags: 9,
  source: 8,
  notes: 14,
  createdAt: 8,
  orders: 5.5,
  value: 8,
  lastOrder: 8.5,
  assignedTo: 10,
});

const DEFAULT_COLUMN_REM = 10;
const GUTTER_REM = 2.75; // mirrors `.record-sheet-gutter` in index.css
const ACTIONS_REM = 3.5;

function remOf(key: string): number {
  return COLUMN_REM[key] ?? DEFAULT_COLUMN_REM;
}

const CELL = 'border-r border-border last:border-r-0 px-3 truncate';
const GUTTER = 'record-sheet-gutter sticky left-0 w-11';
const NUMERIC_TYPES: readonly CustomFieldType[] = ['number', 'money'];

function isNumericField(field: ResolvedCustomField): boolean {
  return field.type !== null && NUMERIC_TYPES.includes(field.type);
}

export interface CustomerTableProps {
  customers: readonly Customer[];
  /** The shop's columns, already ordered and already filtered to what the table shows. */
  fields: readonly ResolvedCustomField[];
  labelOf: (field: ResolvedCustomField) => string;
  sortBy: CustomerSortField;
  sortDir: 'asc' | 'desc';
  onSort: (field: CustomerSortField) => void;
  /** Where a name link takes the counter — carries the search, so Back lands on the list. */
  linkState?: unknown;
  onEdit: (customer: Customer) => void;
  onNewOrder: (customer: Customer) => void;
  onAddFollowUp: (customer: Customer) => void;
}

export function CustomerTable({
  customers,
  fields,
  labelOf,
  sortBy,
  sortDir,
  onSort,
  linkState,
  onEdit,
  onNewOrder,
  onAddFollowUp,
}: CustomerTableProps): ReactElement {
  const { t } = useTranslation();

  const sheetRem = useMemo(
    () =>
      GUTTER_REM +
      ACTIONS_REM +
      fields.reduce((total, field) => total + remOf(field.key), 0) +
      remOf('assignedTo') +
      STAT_KEYS.reduce((total, key) => total + remOf(key), 0),
    [fields],
  );

  /** The boxed cell, spreadsheet-style — a reading aid. */
  const [selectedCell, setSelectedCell] = useState<string | null>(null);
  const cellProps = (
    customer: Customer,
    key: string,
  ): { 'data-cell-selected'?: 'true'; onClick: () => void } => {
    const id = `${customer.id}:${key}`;
    return {
      ...(selectedCell === id ? { 'data-cell-selected': 'true' as const } : {}),
      onClick: () => setSelectedCell(id),
    };
  };

  /** The built-in renderers, keyed by canonical key so a rename never reaches them. */
  const builtinCell = (key: string, customer: Customer): ReactNode => {
    switch (key) {
      case 'primaryPhone': {
        const whatsapp = customer.whatsappPhone ?? customer.primaryPhone;
        return (
          <span className="inline-flex items-center gap-2">
            <a
              href={`tel:${customer.primaryPhone}`}
              className="inline-flex items-center gap-1 hover:underline"
            >
              <Phone aria-hidden="true" className="size-3 shrink-0" />
              {formatMobile(customer.primaryPhone)}
            </a>
            <a
              href={`https://wa.me/91${whatsapp}`}
              target="_blank"
              rel="noreferrer"
              aria-label={t('customers.whatsappTo', { name: customer.name })}
              className="text-muted-foreground hover:text-foreground"
            >
              <MessageCircle aria-hidden="true" className="size-3.5 shrink-0" />
            </a>
          </span>
        );
      }
      case 'kind':
        return t(`customers.kind.${customer.kind}`);
      case 'gstin':
        return customer.gstin ? <span className="font-mono">{customer.gstin}</span> : <Blank />;
      case 'email':
        return customer.email ? (
          <a href={`mailto:${customer.email}`} className="hover:underline">
            {customer.email}
          </a>
        ) : (
          <Blank />
        );
      case 'address':
        return addressOf(customer) ?? <Blank />;
      case 'tags':
        return customer.tags.length > 0 ? (
          <span className="inline-flex min-w-0 gap-1">
            {customer.tags.map((tag) => (
              <span
                key={tag}
                className="shrink-0 rounded-sm bg-secondary px-1.5 py-0.5 text-xs text-secondary-foreground"
              >
                {tag}
              </span>
            ))}
          </span>
        ) : (
          <Blank />
        );
      case 'source':
        return customer.source ? t(`customers.source.${customer.source}`) : <Blank />;
      case 'notes':
        return customer.notes ?? <Blank />;
      case 'createdAt':
        return formatDate(customer.createdAt);
      default:
        return <Blank />;
    }
  };

  const statCell = (key: StatKey, customer: Customer): ReactNode => {
    switch (key) {
      case 'orders':
        return String(customer.orderCount);
      case 'value':
        return formatCurrency(customer.lifetimeValuePaise, { compact: true });
      case 'lastOrder':
        return customer.lastOrderAt ? (
          formatDate(customer.lastOrderAt)
        ) : (
          <span className="text-muted-foreground">{t('customers.neverOrdered')}</span>
        );
    }
  };

  /** One heading cell, sortable when the server can order by it. */
  const heading = (key: string, label: string, numeric: boolean): ReactElement => {
    const sortField = SORT_FIELD_OF[key];
    const active = sortField !== undefined && sortBy === sortField;
    return (
      <th
        key={key}
        scope="col"
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={cn(
          CELL,
          'sticky top-0 z-20 h-10 whitespace-nowrap border-b border-border bg-muted text-left font-medium text-muted-foreground',
          numeric && 'text-right',
          key === 'name' && 'left-11 z-30',
        )}
        style={{ width: `${remOf(key)}rem` }}
      >
        {sortField ? (
          <button
            type="button"
            onClick={() => onSort(sortField)}
            className={cn(
              'inline-flex max-w-full items-center gap-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active && 'text-foreground',
            )}
          >
            <span className="truncate">{label}</span>
            {active ? (
              sortDir === 'asc' ? (
                <ArrowUp aria-hidden="true" className="size-3 shrink-0" />
              ) : (
                <ArrowDown aria-hidden="true" className="size-3 shrink-0" />
              )
            ) : null}
          </button>
        ) : (
          label
        )}
      </th>
    );
  };

  return (
    // The scroll container, not the page: the heading row and the pinned columns stick to it.
    <div className="record-ledger-table max-h-[70dvh] overflow-auto md:max-h-none md:min-h-0 md:flex-1">
      <table
        style={{ minWidth: `${sheetRem}rem` }}
        className="w-full table-fixed border-collapse text-sm"
      >
        <caption className="sr-only">{t('customers.tableCaption')}</caption>
        <thead>
          <tr className="bg-muted">
            <th
              aria-hidden="true"
              className={cn(CELL, GUTTER, 'sticky top-0 z-30 h-10 border-b border-border')}
            />
            {fields.map((field) => heading(field.key, labelOf(field), isNumericField(field)))}
            {heading('assignedTo', t('customers.column.assignedTo'), false)}
            {STAT_KEYS.map((key) =>
              heading(key, t(`customers.column.${key}`), key !== 'lastOrder'),
            )}
            <th
              aria-hidden="true"
              className="record-sheet-spacer sticky top-0 z-20 h-10 bg-muted"
            />
            <th
              scope="col"
              className={cn(
                CELL,
                'record-sheet-actions sticky right-0 top-0 z-30 h-10 w-[3.5rem] border-b border-border bg-muted text-right font-medium text-muted-foreground',
              )}
            >
              <span className="sr-only">{t('customers.column.actions')}</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {customers.map((customer, index) => (
            <tr
              key={customer.id}
              data-testid="customer-row"
              className="record-ledger-row group h-10 border-b border-border last:border-0"
            >
              <td aria-hidden="true" className={cn(CELL, GUTTER, 'z-10')}>
                {index + 1}
              </td>
              {fields.map((field) => {
                if (field.key === 'name') {
                  return (
                    <th
                      key={field.key}
                      scope="row"
                      title={customer.name}
                      className={cn(
                        CELL,
                        'record-ledger-name sticky left-11 z-10 bg-background text-left font-medium',
                      )}
                      {...cellProps(customer, field.key)}
                    >
                      <Link
                        to={`/customers/${customer.id}`}
                        state={linkState}
                        className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {customer.name}
                      </Link>
                    </th>
                  );
                }

                const custom = field.origin === 'custom';
                const numeric = isNumericField(field);
                return (
                  <td
                    key={field.key}
                    className={cn(
                      CELL,
                      numeric && 'text-right tabular-nums',
                      field.key === 'createdAt' && 'text-muted-foreground',
                    )}
                    title={overflowTitle(field, customer)}
                    {...cellProps(customer, field.key)}
                  >
                    {custom ? customCell(field, customer, t) : builtinCell(field.key, customer)}
                  </td>
                );
              })}
              <td
                className={CELL}
                title={customer.assignedToName ?? undefined}
                {...cellProps(customer, 'assignedTo')}
              >
                {customer.assignedToName ?? <Blank />}
              </td>
              {STAT_KEYS.map((key) => (
                <td
                  key={key}
                  className={cn(CELL, key !== 'lastOrder' && 'text-right tabular-nums')}
                  {...cellProps(customer, key)}
                >
                  {statCell(key, customer)}
                </td>
              ))}

              <td aria-hidden="true" className="record-sheet-spacer" />
              <td className={cn(CELL, 'record-sheet-actions sticky right-0 z-10 text-right')}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('customers.rowActions', { name: customer.name })}
                      className="rounded-md p-1.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <MoreHorizontal aria-hidden="true" className="size-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => onEdit(customer)}>
                      {t('common.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => onNewOrder(customer)}>
                      {t('customers.newOrder')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => onAddFollowUp(customer)}>
                      {t('customers.addFollowUp')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A field the shop added, rendered from its type — all we know about it. */
function customCell(
  field: ResolvedCustomField,
  customer: Customer,
  t: (key: string) => string,
): ReactNode {
  const value = customer.custom?.find((entry) => entry.fieldKey === field.key);
  if (!value) return <Blank />;

  switch (field.type) {
    case 'money':
      return value.number === null ? <Blank /> : formatCurrency(value.number);
    case 'number':
      return value.number === null ? <Blank /> : String(value.number);
    case 'date':
      return value.date === null ? <Blank /> : formatDate(value.date);
    case 'boolean':
      if (value.checked === null) return <Blank />;
      return value.checked ? t('common.yes') : t('common.no');
    default:
      return value.text === null || value.text === '' ? <Blank /> : value.text;
  }
}

/** The address on one line, as far as it was filled in. */
function addressOf(customer: Customer): string | null {
  const { line1, line2, city, pincode } = customer.address;
  const parts = [line1, line2, city, pincode].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(', ') : null;
}

/** The full text of a free-text cell a fixed column can cut off. */
function overflowTitle(field: ResolvedCustomField, customer: Customer): string | undefined {
  if (field.origin === 'custom') {
    const value = customer.custom?.find((entry) => entry.fieldKey === field.key);
    return field.type === 'text' && value?.text ? value.text : undefined;
  }
  switch (field.key) {
    case 'notes':
      return customer.notes ?? undefined;
    case 'email':
      return customer.email ?? undefined;
    case 'address':
      return addressOf(customer) ?? undefined;
    case 'tags':
      return customer.tags.length > 0 ? customer.tags.join(', ') : undefined;
    default:
      return undefined;
  }
}

/** An empty cell that reads as empty rather than as a rendering failure. */
function Blank(): ReactElement {
  return <span className="text-muted-foreground/50">—</span>;
}
